import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Calendar, Users, Wallet, FileBarChart2,
  Inbox, ShoppingBag, PenSquare, LogOut, Building2, Ban, Crown, CreditCard,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/logo.png";

const MASTER = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Appointments", icon: Calendar },
  { to: "/admin/pos", label: "POS", icon: ShoppingBag },
  { to: "/admin/membership-requests", label: "Membership Requests", icon: CreditCard },
  { to: "/admin/therapists", label: "Therapists", icon: Users },
  { to: "/admin/wallets", label: "Therapist Wallets", icon: Wallet },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/blacklist", label: "Blacklist", icon: Ban },
  { to: "/admin/cms", label: "Testimonials CMS", icon: PenSquare },
  { to: "/admin/memberships", label: "Memberships CMS", icon: Crown },
  { to: "/admin/branches-cms", label: "Branches CMS", icon: Building2 },
];

const BRANCH = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Appointments", icon: Calendar },
  { to: "/admin/pos", label: "POS", icon: ShoppingBag },
  { to: "/admin/membership-requests", label: "Membership Requests", icon: CreditCard },
  { to: "/admin/therapists", label: "Therapists", icon: Users },
  { to: "/admin/wallets", label: "Wallets", icon: Wallet },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "master_admin" ? MASTER : BRANCH;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-hblack text-white">
      <aside data-testid="admin-sidebar" className="w-64 shrink-0 border-r border-white/10 bg-gradient-to-b from-[#0a0a0a] via-black to-[#0a0a0a] fixed top-0 bottom-0 left-0 flex flex-col justify-between shadow-2xl z-30 h-screen overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="p-6 border-b border-white/10 bg-black/60 shrink-0">
            <div className="flex items-center gap-3 mb-2">
              <img src={logo} alt="Hammam Logo" className="h-10 w-auto object-contain" />
              <span className="font-heading text-sm text-white font-bold tracking-wider">Hammam Men Spa</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-extrabold mt-1">
              {user?.role === "master_admin" ? "Master Console" : "Branch Console"}
            </div>
          </div>

          <nav className="py-4 px-2 space-y-1 overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-thumb-white/20">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border-l-4 ${
                    isActive
                      ? "text-amber-300 border-amber-400 bg-gradient-to-r from-amber-500/20 to-amber-500/5 shadow-md shadow-yellow-950/20 font-bold"
                      : "text-white/70 border-transparent hover:text-amber-300 hover:bg-white/5"
                  }`
                }
              >
                <n.icon size={18} strokeWidth={1.8} />
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="border-t border-white/10 p-4 bg-black/90 shrink-0 z-10">
          <div className="text-xs font-bold text-white mb-0.5 truncate">{user?.name}</div>
          <div className="text-[11px] text-white/50 mb-2.5 truncate">{user?.email}</div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 border border-red-500/40 bg-red-950/30 hover:bg-red-900/50 hover:border-red-400 text-red-300 py-2 rounded-xl text-xs uppercase tracking-widest font-extrabold transition-all shadow-md shadow-red-950/30"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen bg-hblack">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="p-8 lg:p-10"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
